/* LT-HoursPerTrade-01 / -02 — Hours per Trade panel.
 *
 * Mounted on the Adjudication tab between the Cost breakdown card and the
 * Final-adjustment panel.
 *
 * LT-HoursPerTrade-01: Manual top-down labour-effort capture. Estimators
 * input trade × hours × rate and compare against the bottom-up Estimate
 * Labour total from line items.
 *
 * LT-HoursPerTrade-02: Adds a "Pull from estimate" button in the card header
 * that REPLACES the current rows with a server-derived roll-up from
 * recipe-backed estimate line items. Manual edits remain the source of truth
 * (editing a pulled row flips its source back to "manual" on the backend).
 *
 * Design rules captured from /app/memory/LT-HoursPerTrade-0{1,2}_preflight_report.md.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown, ChevronRight, Plus, Trash2, Clock, Loader2, Check,
  Download,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { gbp, num } from "../lib/format";

import {
  Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "./ui/command";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "./ui/tooltip";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const cls = (...a) => a.filter(Boolean).join(" ");
const OVERRIDE_DOT_COLOUR = "#D97706"; // amber-700 — matches RecipeDrawer.flagDot pattern.
const ROUND2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const RATES_EQUAL = (a, b) => Math.abs((Number(a) || 0) - (Number(b) || 0)) < 0.01;

/** Persisted collapse state. Default: collapsed if empty, expanded if rows exist. */
const lsKey = (tenderId) => `lux_hpt_collapsed_${tenderId}`;
const readPersistedCollapsed = (tenderId) => {
  try {
    const v = window.localStorage.getItem(lsKey(tenderId));
    if (v === "true") return true;
    if (v === "false") return false;
  } catch {/* SSR / storage disabled */}
  return null;
};
const writePersistedCollapsed = (tenderId, value) => {
  try {
    window.localStorage.setItem(lsKey(tenderId), value ? "true" : "false");
  } catch {/* ignore */}
};


export default function HoursPerTradePanel({ tenderId }) {
  const [data, setData] = useState(null);
  const [labourRates, setLabourRates] = useState([]);
  const [loading, setLoading] = useState(true);
  // null = not yet derived from data, true/false = explicit state.
  const [collapsed, setCollapsed] = useState(null);
  const [savingRowId, setSavingRowId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Transient draft row (user clicked "Add Trade" — not yet persisted to backend).
  const [draftRow, setDraftRow] = useState(null);
  // LT-HoursPerTrade-02 — pull workflow state machine.
  // pullState: 'idle' | 'confirming' | 'pulling' | 'summarising'
  // pullSummary: response from the backend's pull endpoint (kept for the
  //   post-pull summary dialog). Cleared on close.
  const [pullState, setPullState] = useState("idle");
  const [pullSummary, setPullSummary] = useState(null);

  // Rate-lookup map keyed by trade_id for amber-dot detection + revert.
  const rateById = useMemo(() => {
    const map = {};
    for (const r of labourRates || []) map[r.id] = r;
    return map;
  }, [labourRates]);

  /** Initial load: HPT data + labour rates in parallel. */
  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/tenders/${tenderId}/hours-per-trade`),
      api.get("/labour-rates"),
    ])
      .then(([h, l]) => {
        setData(h.data);
        setLabourRates(l.data || []);
      })
      .catch(() => toast.error("Failed to load Hours per Trade"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tenderId]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Once data has loaded, settle the collapse state. localStorage overrides
   * the empty/has-rows default. */
  useEffect(() => {
    if (!data) return;
    if (collapsed !== null) return; // already decided once
    const persisted = readPersistedCollapsed(tenderId);
    if (persisted !== null) setCollapsed(persisted);
    else setCollapsed((data.rows || []).length === 0);
  }, [data, tenderId, collapsed]);

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    writePersistedCollapsed(tenderId, next);
  };

  // ----- Server mutations -----
  const refreshFromResponse = (resp) => {
    if (!resp?.data) return;
    // The endpoint always returns { rows, totals... } shape.
    const { row, row_id, ...rest } = resp.data; // eslint-disable-line no-unused-vars
    setData(rest);
  };

  const addRow = async ({ trade_id, hours = 0, rate }) => {
    setSavingRowId("__draft__");
    try {
      const body = { trade_id, hours };
      if (rate !== undefined && rate !== null) body.rate = rate;
      const resp = await api.post(`/tenders/${tenderId}/hours-per-trade`, body);
      refreshFromResponse(resp);
      setDraftRow(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to add trade";
      toast.error(typeof msg === "string" ? msg : "Failed to add trade");
    } finally {
      setSavingRowId(null);
    }
  };

  const updateRow = async (rowId, patch) => {
    setSavingRowId(rowId);
    try {
      const resp = await api.put(
        `/tenders/${tenderId}/hours-per-trade/${rowId}`,
        patch,
      );
      refreshFromResponse(resp);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to save row";
      toast.error(typeof msg === "string" ? msg : "Failed to save row");
      // Re-fetch to revert any optimistic UI.
      load();
    } finally {
      setSavingRowId(null);
    }
  };

  const deleteRow = async (rowId) => {
    setSavingRowId(rowId);
    try {
      const resp = await api.delete(
        `/tenders/${tenderId}/hours-per-trade/${rowId}`,
      );
      refreshFromResponse(resp);
      toast.success("Trade removed");
    } catch {
      toast.error("Failed to remove trade");
    } finally {
      setSavingRowId(null);
      setDeleteTarget(null);
    }
  };

  const handleStartAdd = () => {
    if (collapsed) {
      setCollapsed(false);
      writePersistedCollapsed(tenderId, false);
    }
    if (draftRow) return; // only one draft at a time
    setDraftRow({ id: "__draft__", trade_id: "", trade_name: "", hours: 0, rate: 0, value: 0 });
  };

  // ----- LT-HoursPerTrade-02 — pull-from-estimate ---------------------------
  /** Click on the "Pull from estimate" button -> opens the pre-pull
   * confirmation AlertDialog. The dialog body wording differs depending on
   * whether the user already has rows in the panel (see PullConfirmDialog). */
  const handleStartPull = () => {
    // Auto-cancel an in-progress draft row so the pull doesn't compete with
    // a half-filled trade picker.
    if (draftRow) setDraftRow(null);
    setPullState("confirming");
  };

  /** Confirm handler — fires the POST and transitions through 'pulling' →
   * 'summarising' (success) or back to 'idle' (error, with toast). */
  const handleConfirmPull = async () => {
    setPullState("pulling");
    try {
      const resp = await api.post(
        `/tenders/${tenderId}/hours-per-trade/pull-from-estimate`,
      );
      // Refresh the table from the same response shape used elsewhere.
      const { pull_summary, ...rest } = resp.data || {};
      setData(rest);
      setPullSummary(pull_summary || null);
      setPullState("summarising");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Failed to pull from estimate — existing rows preserved.",
      );
      setPullState("idle");
    }
  };

  const handleClosePullSummary = () => {
    setPullState("idle");
    setPullSummary(null);
  };

  // ----- Derived ----- (used by the header summary and footer)
  const rows = data?.rows || [];
  const tradeCount = rows.length;
  const hptTotal = data?.hours_per_trade_total ?? 0;
  const hptHours = data?.hours_per_trade_total_hours ?? 0;
  const estimateLabour = data?.estimate_labour_total ?? 0;
  const difference = data?.difference ?? 0;
  const differencePct = data?.difference_pct; // null when estimate = 0

  // ----- Loading skeleton -----
  if (loading || collapsed === null) {
    return (
      <div className="rounded-lg border border-line bg-surface p-4" data-testid="hpt-card">
        <div className="flex items-center gap-2">
          <Clock size={14} className="text-ink-4" />
          <span className="text-ink-3" style={{ fontSize: 12.5 }}>Loading Hours per Trade…</span>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="rounded-lg border border-line bg-surface overflow-hidden" data-testid="hpt-card">
        {/* ----- Card header — split into LEFT (collapse-toggle) + RIGHT
            (pull button + summary). Splitting avoids nested-button a11y
            warnings and gives us a stable hit area for the Pull button. -----*/}
        <div className="w-full flex items-center justify-between px-4 py-3">
          <button
            type="button"
            onClick={toggleCollapse}
            className="flex items-center gap-2 text-left hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
            data-testid="hpt-collapse-toggle"
            aria-expanded={!collapsed}
          >
            {collapsed ? (
              <ChevronRight size={14} className="text-ink-3" />
            ) : (
              <ChevronDown size={14} className="text-ink-3" />
            )}
            <Clock size={14} className="text-ink-4" />
            <span className="text-ink" style={{ fontSize: 13.5, fontWeight: 600 }}>
              Hours per Trade
            </span>
            <span className="text-ink-4" style={{ fontSize: 11 }}>
              · Manual entry of trade-level labour hours
            </span>
          </button>
          <div className="flex items-center gap-3">
            {/* Pull-from-estimate button: visible only when the card is
                expanded. Hidden when collapsed so the summary text on the
                right keeps the card compact. */}
            {!collapsed && (
              <button
                type="button"
                onClick={handleStartPull}
                disabled={pullState === "pulling"}
                className={cls(
                  "h-7 px-2.5 rounded-md border border-line bg-surface flex items-center gap-1.5",
                  "text-ink-2 hover:text-ink hover:bg-surface-2/60",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent",
                  pullState === "pulling" && "opacity-70 cursor-wait",
                )}
                style={{ fontSize: 11.5, fontWeight: 500 }}
                data-testid="hpt-pull-btn"
                aria-label="Pull hours per trade from estimate"
              >
                {pullState === "pulling" ? (
                  <>
                    <Loader2 size={12} className="animate-spin" /> Pulling…
                  </>
                ) : (
                  <>
                    <Download size={12} /> Pull from estimate
                  </>
                )}
              </button>
            )}
            <div className="text-ink-3 tabular-nums" style={{ fontSize: 11.5 }} data-testid="hpt-header-summary">
              {tradeCount === 0
                ? "No rows added"
                : `${gbp(hptTotal)} · ${num(hptHours, hptHours % 1 === 0 ? 0 : 1)} hrs · ${tradeCount} ${tradeCount === 1 ? "trade" : "trades"}`}
            </div>
          </div>
        </div>

        {/* ----- Card body ----- */}
        {!collapsed && (
          <div className="border-t border-line">
            {tradeCount === 0 && !draftRow ? (
              <EmptyState onAdd={handleStartAdd} />
            ) : (
              <RowsTable
                rows={rows}
                draftRow={draftRow}
                labourRates={labourRates}
                rateById={rateById}
                savingRowId={savingRowId}
                onPickTradeForDraft={(trade) => addRow({ trade_id: trade.id })}
                onCancelDraft={() => setDraftRow(null)}
                onUpdate={updateRow}
                onRequestDelete={(row) => setDeleteTarget(row)}
              />
            )}

            <div className="px-4 py-3 border-t border-line">
              <button
                type="button"
                onClick={handleStartAdd}
                disabled={!!draftRow}
                className={cls(
                  "w-full h-9 rounded-md border border-dashed flex items-center justify-center gap-1.5",
                  draftRow
                    ? "border-line/60 text-ink-4 cursor-not-allowed"
                    : "border-line text-ink-3 hover:text-ink-2 hover:bg-surface-2/50",
                )}
                style={{ fontSize: 12 }}
                data-testid="hpt-add-trade-btn"
              >
                <Plus size={13} /> Add Trade
              </button>
            </div>

            {/* ----- Totals footer ----- */}
            <TotalsFooter
              hptTotal={hptTotal}
              hptHours={hptHours}
              estimateLabour={estimateLabour}
              difference={difference}
              differencePct={differencePct}
            />
          </div>
        )}
      </div>

      {/* ----- Delete confirmation ----- */}
      {deleteTarget && (
        <Dialog open={true} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <DialogContent className="max-w-md" data-testid="hpt-delete-confirm-dialog">
            <DialogHeader>
              <DialogTitle>
                Remove {deleteTarget.trade_name ? `'${deleteTarget.trade_name}'` : "row"}?
              </DialogTitle>
            </DialogHeader>
            <p style={{ fontSize: 13 }}>
              Remove {deleteTarget.trade_name ? `'${deleteTarget.trade_name}'` : "this trade"} hours? This cannot be undone.
            </p>
            <DialogFooter>
              <button
                onClick={() => setDeleteTarget(null)}
                className="h-8 px-3 rounded-md border border-line text-ink-2 hover:bg-surface-2"
                style={{ fontSize: 12 }}
                data-testid="hpt-delete-confirm-cancel"
              >Cancel</button>
              <button
                onClick={() => deleteRow(deleteTarget.id)}
                className="h-8 px-3.5 rounded-md text-white shadow-soft"
                style={{ fontSize: 12, fontWeight: 500, background: "#A1311E" }}
                data-testid="hpt-delete-confirm-yes"
              >Remove</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ----- LT-HoursPerTrade-02 — pre-pull confirmation (AlertDialog) ----- */}
      <PullConfirmDialog
        open={pullState === "confirming"}
        rows={rows}
        hptTotal={hptTotal}
        hptHours={hptHours}
        onCancel={() => setPullState("idle")}
        onConfirm={handleConfirmPull}
      />

      {/* ----- LT-HoursPerTrade-02 — post-pull summary (Dialog) ----- */}
      <PullSummaryDialog
        open={pullState === "summarising"}
        summary={pullSummary}
        hptTotal={hptTotal}
        onClose={handleClosePullSummary}
      />
    </TooltipProvider>
  );
}


/* ---------- Empty state ---------- */
function EmptyState({ onAdd }) {
  return (
    <div className="px-4 py-8 flex flex-col items-center" data-testid="hpt-empty-state">
      <Clock size={20} className="text-ink-4 mb-2" />
      <div className="text-ink" style={{ fontSize: 13.5, fontWeight: 500 }}>
        No trades added yet
      </div>
      <div className="text-ink-3 mt-1" style={{ fontSize: 12 }}>
        Click Add Trade below to start capturing labour hours.
      </div>
    </div>
  );
}


/* ---------- Rows table ---------- */
function RowsTable({
  rows, draftRow, labourRates, rateById, savingRowId,
  onPickTradeForDraft, onCancelDraft, onUpdate, onRequestDelete,
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full" style={{ fontSize: 12.5 }}>
        <thead>
          <tr className="border-b border-line bg-surface-2/40">
            <th className="col-head px-4 py-2 text-left" style={{ fontSize: 10, width: 40 }}>#</th>
            <th className="col-head px-4 py-2 text-left" style={{ fontSize: 10 }}>Trade</th>
            <th className="col-head px-4 py-2 text-right" style={{ fontSize: 10, width: 120 }}>Hours</th>
            <th className="col-head px-4 py-2 text-right" style={{ fontSize: 10, width: 160 }}>Rate £/hr</th>
            <th className="col-head px-4 py-2 text-right" style={{ fontSize: 10, width: 160 }}>Value £</th>
            <th className="col-head px-4 py-2 text-right" style={{ fontSize: 10, width: 48 }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <DataRow
              key={row.id}
              row={row}
              index={idx + 1}
              labourRates={labourRates}
              libraryRate={Number(rateById[row.trade_id]?.hourly_rate ?? row.rate)}
              archivedTrade={!rateById[row.trade_id]}
              saving={savingRowId === row.id}
              onChangeTrade={(trade) => onUpdate(row.id, { trade_id: trade.id })}
              onChangeHours={(v) => onUpdate(row.id, { hours: v })}
              onChangeRate={(v) => onUpdate(row.id, { rate: v })}
              onRequestDelete={() => onRequestDelete(row)}
            />
          ))}
          {draftRow && (
            <DraftRow
              index={rows.length + 1}
              labourRates={labourRates}
              saving={savingRowId === "__draft__"}
              onPickTrade={onPickTradeForDraft}
              onCancel={onCancelDraft}
            />
          )}
        </tbody>
      </table>
    </div>
  );
}


/* ---------- Persistent row (server-stored) ---------- */
function DataRow({
  row, index, labourRates, libraryRate, archivedTrade, saving,
  onChangeTrade, onChangeHours, onChangeRate, onRequestDelete,
}) {
  // Local "draft" buffers for the numeric inputs so the user can type freely
  // without each keystroke firing a PUT. Saved on blur.
  const [hoursLocal, setHoursLocal] = useState(String(row.hours ?? 0));
  const [rateLocal, setRateLocal] = useState(String(row.rate ?? 0));
  useEffect(() => setHoursLocal(String(row.hours ?? 0)), [row.hours]);
  useEffect(() => setRateLocal(String(row.rate ?? 0)), [row.rate]);

  // Live client-side preview of `value` as the user types. Replaced by the
  // authoritative server value once a save round-trips.
  const liveValue = useMemo(() => {
    const h = Number(hoursLocal || 0);
    const r = Number(rateLocal || 0);
    return ROUND2(h * r);
  }, [hoursLocal, rateLocal]);

  const hoursInvalid = Number(hoursLocal) < 0;
  const rateInvalid = Number(rateLocal) < 0;

  // Override-dot visibility:
  //   • Archived trade (no live library entry) → always show the dot with the
  //     LT-HoursPerTrade-02 archived-trade tooltip. Click is a no-op since
  //     there's no library rate to revert to.
  //   • Active trade with a divergent rate → original LT-01 behaviour.
  const isOverridden = archivedTrade
    ? true
    : !RATES_EQUAL(Number(rateLocal), libraryRate);

  const commitHours = () => {
    const v = Number(hoursLocal || 0);
    if (hoursInvalid) { setHoursLocal(String(row.hours ?? 0)); return; }
    if (ROUND2(v) === ROUND2(row.hours)) return;
    onChangeHours(ROUND2(v));
  };
  const commitRate = () => {
    const v = Number(rateLocal || 0);
    if (rateInvalid) { setRateLocal(String(row.rate ?? 0)); return; }
    if (ROUND2(v) === ROUND2(row.rate)) return;
    onChangeRate(ROUND2(v));
  };
  const revertToLibraryRate = () => {
    if (!isOverridden) return;
    if (archivedTrade) return; // no library rate to revert to
    setRateLocal(String(libraryRate));
    onChangeRate(ROUND2(libraryRate));
  };

  return (
    <tr className="border-b border-line/60 last:border-0" data-testid={`hpt-row-${row.id}`}>
      <td className="px-4 py-2 text-ink-4 tabular-nums" style={{ fontSize: 12 }}>{index}</td>
      <td className="px-4 py-2">
        <TradePicker
          value={row.trade_id}
          valueName={row.trade_name}
          labourRates={labourRates}
          onPick={onChangeTrade}
          testIdPrefix={`hpt-row-${row.id}`}
        />
      </td>
      <td className="px-4 py-2">
        <NumberCell
          value={hoursLocal}
          onChange={setHoursLocal}
          onBlur={commitHours}
          invalid={hoursInvalid}
          step={0.5}
          testId={`hpt-row-${row.id}-hours`}
        />
      </td>
      <td className="px-4 py-2">
        <div className="flex items-center justify-end gap-1.5">
          {isOverridden && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={revertToLibraryRate}
                  className={cls(
                    "inline-flex items-center justify-center",
                    archivedTrade ? "cursor-default" : "cursor-pointer",
                  )}
                  style={{ width: 12, height: 12 }}
                  data-testid={`hpt-row-${row.id}-rate-dot`}
                  aria-label={archivedTrade ? "Trade no longer in library" : "Rate overridden"}
                >
                  <span
                    className="rounded-full"
                    style={{ width: 6, height: 6, background: OVERRIDE_DOT_COLOUR }}
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" style={{ fontSize: 11 }}>
                {archivedTrade
                  ? "Trade no longer in Labour Library; rate sourced from estimate recipe"
                  : `Rate overridden for this row (library: ${gbp(libraryRate)}). Click to revert.`}
              </TooltipContent>
            </Tooltip>
          )}
          <NumberCell
            value={rateLocal}
            onChange={setRateLocal}
            onBlur={commitRate}
            invalid={rateInvalid}
            step={0.5}
            prefix="£"
            testId={`hpt-row-${row.id}-rate`}
          />
        </div>
      </td>
      <td
        className="px-4 py-2 text-right tabular-nums text-ink"
        data-testid={`hpt-row-${row.id}-value`}
      >
        {gbp(liveValue)}
      </td>
      <td className="px-4 py-2 text-right">
        <div className="flex items-center justify-end gap-1.5">
          {saving && <Loader2 size={12} className="animate-spin text-ink-4" />}
          <button
            type="button"
            onClick={onRequestDelete}
            className="text-ink-4 hover:text-note-red"
            data-testid={`hpt-row-${row.id}-delete`}
            aria-label={`Remove ${row.trade_name}`}
          >
            <Trash2 size={13} />
          </button>
        </div>
      </td>
    </tr>
  );
}


/* ---------- Transient draft row (not yet POSTed) ---------- */
function DraftRow({ index, labourRates, saving, onPickTrade, onCancel }) {
  return (
    <tr className="border-b border-line/60 bg-accent/5" data-testid="hpt-draft-row">
      <td className="px-4 py-2 text-ink-4 tabular-nums" style={{ fontSize: 12 }}>{index}</td>
      <td className="px-4 py-2">
        <TradePicker
          value=""
          valueName=""
          labourRates={labourRates}
          openOnMount
          onPick={onPickTrade}
          onClose={onCancel}
          testIdPrefix="hpt-draft"
          placeholder="Pick a trade…"
        />
      </td>
      <td className="px-4 py-2 text-right text-ink-4 tabular-nums" style={{ fontSize: 12 }}>—</td>
      <td className="px-4 py-2 text-right text-ink-4 tabular-nums" style={{ fontSize: 12 }}>—</td>
      <td className="px-4 py-2 text-right text-ink-4 tabular-nums" style={{ fontSize: 12 }}>—</td>
      <td className="px-4 py-2 text-right">
        <div className="flex items-center justify-end gap-1.5">
          {saving ? (
            <Loader2 size={12} className="animate-spin text-ink-4" />
          ) : (
            <button
              type="button"
              onClick={onCancel}
              className="text-ink-4 hover:text-note-red"
              aria-label="Cancel new trade"
              data-testid="hpt-draft-cancel"
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}


/* ---------- Trade picker (Popover + Command combobox) ---------- */
function TradePicker({
  value, valueName, labourRates, onPick, onClose,
  openOnMount = false, placeholder = "Select trade…", testIdPrefix = "hpt-trade",
}) {
  const [open, setOpen] = useState(false);
  const armedRef = useRef(false);

  // Auto-open on mount when requested (for new draft rows).
  useEffect(() => {
    if (openOnMount && !armedRef.current) {
      armedRef.current = true;
      // Brief delay so the popover anchors after the row has been mounted.
      const t = setTimeout(() => setOpen(true), 30);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [openOnMount]);

  const handleOpenChange = (o) => {
    setOpen(o);
    if (!o && openOnMount && !value && onClose) onClose();
  };

  const handlePick = (trade) => {
    setOpen(false);
    onPick(trade);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cls(
            "w-full h-8 px-2.5 rounded-md border border-line bg-surface text-left",
            "flex items-center justify-between hover:bg-surface-2/40",
            "focus:outline-none focus:ring-1 focus:ring-accent",
          )}
          style={{ fontSize: 12.5 }}
          data-testid={`${testIdPrefix}-trigger`}
        >
          <span className={value ? "text-ink" : "text-ink-4"}>
            {valueName || placeholder}
          </span>
          <ChevronDown size={12} className="text-ink-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-[280px]"
        data-testid={`${testIdPrefix}-popover`}
      >
        <Command>
          <CommandInput placeholder="Search trades…" data-testid={`${testIdPrefix}-search`} />
          <CommandList>
            <CommandEmpty>No trade matches.</CommandEmpty>
            <CommandGroup>
              {(labourRates || []).map((rate) => (
                <CommandItem
                  key={rate.id}
                  value={rate.trade_name}
                  onSelect={() => handlePick(rate)}
                  data-testid={`${testIdPrefix}-option-${rate.id}`}
                >
                  <Check
                    size={12}
                    className={cls(
                      "mr-1.5",
                      value === rate.id ? "opacity-100 text-accent" : "opacity-0",
                    )}
                  />
                  <span className="flex-1">{rate.trade_name}</span>
                  <span className="text-ink-4 tabular-nums" style={{ fontSize: 11 }}>
                    {gbp(rate.hourly_rate)}/hr
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}


/* ---------- Generic numeric cell input ---------- */
function NumberCell({ value, onChange, onBlur, invalid, step = 1, prefix, testId }) {
  return (
    <div className="relative">
      {prefix && (
        <span
          className="absolute left-2 top-1/2 -translate-y-1/2 text-ink-4"
          style={{ fontSize: 12 }}
        >
          {prefix}
        </span>
      )}
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.currentTarget.blur();
          }
        }}
        step={step}
        min={0}
        className={cls(
          "w-full h-8 rounded-md border bg-surface text-ink tabular-nums text-right",
          "focus:outline-none focus:ring-1 focus:ring-accent",
          prefix ? "pl-5 pr-2" : "px-2",
          invalid ? "border-note-red" : "border-line",
        )}
        style={{ fontSize: 12.5 }}
        data-testid={testId}
      />
    </div>
  );
}


/* ---------- Totals footer ---------- */
function TotalsFooter({ hptTotal, hptHours, estimateLabour, difference, differencePct }) {
  const diffSign = difference > 0 ? "+" : "";
  const diffPctDisplay = differencePct === null || differencePct === undefined
    ? "n/a"
    : `${differencePct > 0 ? "+" : ""}${num(differencePct, 1)}%`;
  const hoursDisplay = hptHours % 1 === 0 ? num(hptHours, 0) : num(hptHours, 1);

  return (
    <div className="border-t border-line bg-surface-2/40 px-4 py-3">
      <div className="grid grid-cols-3 gap-4">
        <div data-testid="hpt-total-hpt">
          <div className="col-head" style={{ fontSize: 10 }}>Hours per Trade total</div>
          <div className="text-ink tabular-nums mt-1" style={{ fontSize: 14, fontWeight: 600 }}>
            {gbp(hptTotal)} <span className="text-ink-3 font-normal" style={{ fontSize: 12 }}>· {hoursDisplay} hrs</span>
          </div>
        </div>
        <div data-testid="hpt-total-estimate">
          <div className="col-head" style={{ fontSize: 10 }}>Estimate Labour (line items)</div>
          <div className="text-ink tabular-nums mt-1" style={{ fontSize: 14, fontWeight: 600 }}>
            {gbp(estimateLabour)}
          </div>
        </div>
        <div data-testid="hpt-total-difference">
          <div className="col-head" style={{ fontSize: 10 }}>Difference</div>
          <div className="text-ink tabular-nums mt-1" style={{ fontSize: 14, fontWeight: 600 }}>
            {diffSign}{gbp(difference)}{" "}
            <span className="text-ink-3 font-normal" style={{ fontSize: 12 }}>
              ({diffPctDisplay})
            </span>
          </div>
        </div>
      </div>
      <div className="text-ink-4 mt-2" style={{ fontSize: 11 }}>
        Hours per trade is your top-down view; line items are the bottom-up roll-up. Differences are expected.
      </div>
    </div>
  );
}



/* =====================================================================
 * LT-HoursPerTrade-02 — Pre-pull confirmation (AlertDialog)
 * =====================================================================
 *
 * Two body variants depending on whether the user already has rows in the
 * panel. Wording aligned with the LT-HoursPerTrade-02 spec §4 (pre-pull
 * confirmation dialog).
 */
function PullConfirmDialog({ open, rows, hptTotal, hptHours, onCancel, onConfirm }) {
  const hasRows = (rows || []).length > 0;
  const tradeCount = (rows || []).length;
  const hoursDisplay = hptHours % 1 === 0 ? num(hptHours, 0) : num(hptHours, 1);

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <AlertDialogContent
        className="max-w-md"
        data-testid="hpt-pull-confirm-dialog"
      >
        <AlertDialogHeader>
          <AlertDialogTitle>
            {hasRows ? "Replace hours per trade?" : "Pull hours from estimate?"}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3" style={{ fontSize: 13, lineHeight: 1.45 }}>
              {hasRows ? (
                <>
                  <p>
                    This will <strong>replace all {tradeCount} {tradeCount === 1 ? "row" : "rows"}</strong>{" "}
                    currently in Hours per Trade ({gbp(hptTotal)} · {hoursDisplay} hrs)
                    with a fresh roll-up from recipe-backed estimate lines.
                  </p>
                  <p className="text-ink-3" style={{ fontSize: 12 }}>
                    Any manual edits to existing rows will be lost. The previous
                    state is preserved in the tender history for reference.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    Auto-populates Hours per Trade by reading labour data from
                    recipe-backed line items in this tender's estimate.
                  </p>
                  <p className="text-ink-3" style={{ fontSize: 12 }}>
                    <strong>Included:</strong> library line items with labour recipes.
                  </p>
                  <p className="text-ink-3" style={{ fontSize: 12 }}>
                    <strong>Skipped:</strong> ad-hoc lines, subby lines, prelims,
                    and prov sums.
                  </p>
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="hpt-pull-confirm-cancel">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            data-testid="hpt-pull-confirm-yes"
          >
            {hasRows ? "Replace with pulled values" : "Pull from estimate"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}


/* =====================================================================
 * LT-HoursPerTrade-02 — Post-pull summary dialog
 * =====================================================================
 *
 * Informational only — single "Done" CTA. Renders skipped-line breakdown
 * inline; the whole skipped block is hidden when every counter is zero.
 * Empty-estimate case (`lines_included===0 && all skip-counts===0`) shows
 * the dedicated guidance message from the spec.
 */
function PullSummaryDialog({ open, summary, hptTotal, onClose }) {
  if (!summary) return null;

  const tradesPulled = summary.trades_pulled || 0;
  const hoursPulled = summary.total_hours_pulled || 0;
  const linesIncluded = summary.lines_included || 0;
  const skipped = summary.lines_skipped || {};
  const skipTotal = Object.values(skipped).reduce((sum, n) => sum + (n || 0), 0);
  const hoursDisplay = hoursPulled % 1 === 0 ? num(hoursPulled, 0) : num(hoursPulled, 1);
  const tradeWord = tradesPulled === 1 ? "trade" : "trades";
  const lineWord = linesIncluded === 1 ? "line" : "lines";

  // Empty-estimate case — no included AND no skipped lines means there were
  // no recipe-backed line items in the tender at all.
  const isEmptyEstimate = linesIncluded === 0 && skipTotal === 0;

  const skipLabels = [
    { key: "ad_hoc",                label: "Ad-hoc lines",          help: "no structured hours data" },
    { key: "subby",                 label: "Subby lines",           help: "subcontractor time, not your trades" },
    { key: "prelims",               label: "Prelims",               help: "separate cost category" },
    { key: "prov_sums",             label: "Pro sums",              help: "allowances, not priced hours" },
    { key: "recipe_without_labour", label: "Library lines (no labour)", help: "no labour entries in recipe" },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="hpt-pull-summary-dialog">
        <DialogHeader>
          <DialogTitle>Pull complete</DialogTitle>
        </DialogHeader>
        <div className="space-y-3" style={{ fontSize: 13, lineHeight: 1.5 }}>
          {isEmptyEstimate ? (
            <p data-testid="hpt-pull-summary-empty">
              No recipe-backed line items found in this estimate. Add manual
              rows below to capture labour hours.
            </p>
          ) : (
            <>
              <p data-testid="hpt-pull-summary-totals">
                Pulled <strong>{tradesPulled} {tradeWord}</strong> — {hoursDisplay} hrs ({gbp(hptTotal)})
                from <strong>{linesIncluded} {lineWord}</strong>.
              </p>
              {skipTotal > 0 && (
                <div data-testid="hpt-pull-summary-skipped">
                  <div className="col-head" style={{ fontSize: 10 }}>
                    Lines skipped
                  </div>
                  <ul className="mt-1 space-y-1">
                    {skipLabels
                      .filter(({ key }) => (skipped[key] || 0) > 0)
                      .map(({ key, label, help }) => (
                        <li
                          key={key}
                          className="flex items-baseline justify-between gap-3"
                          data-testid={`hpt-pull-skipped-${key}`}
                        >
                          <span className="text-ink-2" style={{ fontSize: 12 }}>
                            {label}
                            <span className="text-ink-4" style={{ fontSize: 11 }}>
                              {" "}— {help}
                            </span>
                          </span>
                          <span className="tabular-nums text-ink" style={{ fontSize: 12 }}>
                            {num(skipped[key], 0)}
                          </span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
              <p className="text-ink-4" style={{ fontSize: 11 }}>
                Pulled rows behave like manual rows — adjust hours, override
                rates, or delete as needed.
              </p>
            </>
          )}
        </div>
        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="h-8 px-3.5 rounded-md text-white shadow-soft"
            style={{ fontSize: 12, fontWeight: 500, background: "#2A3B4D" }}
            data-testid="hpt-pull-summary-done"
          >
            Done
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
